import React, { useEffect, useRef, useState } from "react";
import { Table, Spinner } from "reactstrap";

/**
 * Resident list — Unit Number, Name, Contact Number, Email.
 *
 * Contact Number and Email are editable in place for CURRENT residents and save
 * straight back to the live mastersheet on blur or Enter. Past residents come
 * from archived items and historic snapshots, which are records of what was true
 * at the time, so their contact is shown read-only.
 */
const cell = (value) => (String(value ?? "").trim() ? String(value).trim() : "—");

const rowKey = (row, i) => `${row.buildingName}-${row.unitNo}-${row.name}-${row.moveIn}-${i}`;

/** Only live, current tenancies can be written back to. */
export const isRowEditable = (row) => row?.status === "current" && row?.source === "active";

const EditableCell = ({ row, field, type, placeholder, onSave }) => {
  const original = row[field] ?? "";
  const [value, setValue] = useState(original);
  const [state, setState] = useState("idle"); // idle | saving | saved | error
  const inputRef = useRef(null);

  // Keep in step when the directory reloads underneath us.
  useEffect(() => {
    setValue(row[field] ?? "");
  }, [row[field]]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = async () => {
    const next = String(value ?? "").trim();
    if (next === String(original ?? "").trim()) return;
    setState("saving");
    try {
      await onSave(next);
      setState("saved");
      setTimeout(() => setState((s) => (s === "saved" ? "idle" : s)), 1600);
    } catch (err) {
      setState("error");
      setValue(original ?? ""); // put the old value back so nothing looks saved
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      inputRef.current?.blur();
    }
    if (e.key === "Escape") {
      setValue(original ?? "");
      setState("idle");
      inputRef.current?.blur();
    }
  };

  const border =
    state === "error" ? "#DC2626" : state === "saved" ? "#16A34A" : "transparent";

  return (
    <span className="rd-edit">
      <input
        ref={inputRef}
        type={type}
        className="rd-edit-input"
        style={{ borderColor: border }}
        value={value}
        placeholder={placeholder}
        aria-label={`${field === "phone" ? "Contact number" : "Email"} for ${row.name}`}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        disabled={state === "saving"}
      />
      {state === "saving" && <Spinner size="sm" className="rd-edit-spin" />}
      {state === "saved" && (
        <i className="mdi mdi-check rd-edit-ok" title="Saved to the mastersheet" />
      )}
      {state === "error" && (
        <i className="mdi mdi-alert-circle-outline rd-edit-err" title="Could not save — reverted" />
      )}
    </span>
  );
};

const ResidentTable = ({ rows, onSaveContact }) => (
  <div className="rd-table-wrap">
    <Table responsive className="rd-table mb-0">
      <thead>
        <tr>
          <th scope="col">Unit Number</th>
          <th scope="col">Name</th>
          <th scope="col">Contact Number</th>
          <th scope="col">Email</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const editable = isRowEditable(row) && typeof onSaveContact === "function";
          return (
            <tr key={rowKey(row, i)}>
              <td>{cell(row.unitNo)}</td>
              <td>{cell(row.name)}</td>
              <td>
                {editable ? (
                  <EditableCell
                    row={row}
                    field="phone"
                    type="tel"
                    placeholder="Add number"
                    onSave={(v) => onSaveContact(row, { phone: v })}
                  />
                ) : (
                  cell(row.phone)
                )}
              </td>
              <td>
                {editable ? (
                  <EditableCell
                    row={row}
                    field="email"
                    type="email"
                    placeholder="Add email"
                    onSave={(v) => onSaveContact(row, { email: v })}
                  />
                ) : (
                  cell(row.email)
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  </div>
);

export { ResidentTable };
